import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { isAdminEmail } from '../config/adminConfig';
import { adminInviteService } from '../services/adminInviteService';
import {
  IMPERSONATION_STORAGE_KEY,
  setImpersonationReadOnly,
} from '../utils/impersonationGuard';

interface AdminContextType {
  isAdmin: boolean | null;
  isCheckingAdmin: boolean;
  adminEmail: string | null;
  impersonatedUserId: string | null;
  setImpersonatedUserId: (userId: string | null) => void;
  /** True while an admin is viewing another user's account. Impersonation is read-only. */
  isImpersonating: boolean;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: null,
  isCheckingAdmin: true,
  adminEmail: null,
  impersonatedUserId: null,
  setImpersonatedUserId: () => {},
  isImpersonating: false,
});

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: React.ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  
  // Initialize impersonatedUserId from sessionStorage
  const [impersonatedUserId, setImpersonatedUserIdState] = useState<string | null>(() => {
    return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  });

  const setImpersonatedUserId = (userId: string | null) => {
    if (userId) {
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, userId);
    } else {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
    // Impersonation is read-only: keep the service-layer write guard in step with
    // the flag so no mutation can be attributed to the wrong user.
    setImpersonationReadOnly(!!userId);
    setImpersonatedUserIdState(userId);
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (loading) {
        return; // Wait for auth to finish loading
      }

      if (!currentUser) {
        setIsAdmin(false);
        setAdminEmail(null);
        setIsCheckingAdmin(false);
        // Clear impersonation if user logs out
        setImpersonatedUserId(null);
        return;
      }

      const userEmail = currentUser.email;
      if (!userEmail) {
        setIsAdmin(false);
        setAdminEmail(null);
        setIsCheckingAdmin(false);
        setImpersonatedUserId(null);
        return;
      }

      try {
        const adminStatus = await adminInviteService.isEmailAdmin(userEmail);
        setIsAdmin(adminStatus);
        setAdminEmail(adminStatus ? userEmail : null);

        if (adminStatus && currentUser) {
          try {
            await adminInviteService.ensureAdminUserUidSync(currentUser);
          } catch (syncErr) {
            console.warn('ensureAdminUserUidSync:', syncErr);
          }
        }

        // If not admin anymore, clear impersonation
        if (!adminStatus) {
           setImpersonatedUserId(null);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setAdminEmail(null);
        setImpersonatedUserId(null);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, loading]);

  const value: AdminContextType = {
    isAdmin,
    isCheckingAdmin,
    adminEmail,
    impersonatedUserId,
    setImpersonatedUserId,
    isImpersonating: !!impersonatedUserId,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
